type SpecialistBookingFrontendPreviewEnvironment = Readonly<{
  publicOverride?: string;
  nodeEnv?: string;
}>;

export interface SpecialistBookingFrontendPreviewSource {
  sourceLocationId: string;
  sourceUserLocationId: string;
}

/**
 * Exact Specialist sources whose deterministic fixture composition is complete
 * through both Individual and Group descendants. Keeping this allow-list
 * separate from the environment switch prevents a partially authored fixture
 * from replacing the established Services fallback with a dead-end gateway.
 */
const FRONTEND_PREVIEW_SOURCE_ASSIGNMENTS = new Set([
  "venue-glow-room\u0000user-location-andrada-ivan-glow-room",
]);

/**
 * Results may preview the shared Individual engine for every source whose
 * exact Results, catalog, SLG and scheduling evidence is authored. This is a
 * narrower entry-point gate and never expands the general Specialist Profile
 * preview or any Group release ledger.
 */
const RESULTS_FRONTEND_PREVIEW_SOURCE_ASSIGNMENTS = new Set([
  "venue-glow-room\u0000user-location-andrada-ivan-glow-room",
  "venue-pure-spa\u0000user-location-alexandra-roncea-pure-spa",
  "venue-gymone4spa\u0000user-location-gabriela-basaraba-gymone4spa",
  "venue-pure-spa\u0000user-location-maria-habaci-pure-spa",
  "venue-3rd-generation\u0000user-location-radu-preda-3rd-generation",
  "venue-moonlight-nails\u0000user-location-ioana-marinescu-moonlight-nails",
  "venue-velvet-studio\u0000user-location-daria-pop-velvet-studio",
  "venue-skin-theory\u0000user-location-elena-munteanu-skin-theory",
  "venue-atelier-coafura\u0000user-location-teodora-ionescu-atelier-coafura",
  "venue-hair-hood\u0000user-location-catalin-stoica-hair-hood",
  "venue-nova-barber\u0000user-location-catalin-stoica-nova-barber",
  "venue-helen-beauty\u0000user-location-bianca-dumitrescu-helen-beauty",
  "venue-kinetica-wellness\u0000user-location-larisa-ene-kinetica-wellness",
]);

/**
 * Resolves the frontend-only Specialist booking preview independently from the
 * atomic public release ledgers. Development defaults to the deterministic
 * fixture composition; production and tests remain closed unless an explicit
 * public preview environment opts in.
 */
export function resolveSpecialistBookingFrontendPreviewEnabled({
  publicOverride,
  nodeEnv,
}: SpecialistBookingFrontendPreviewEnvironment = {
  publicOverride:
    process.env.NEXT_PUBLIC_VOLERA_SPECIALIST_BOOKING_PREVIEW,
  nodeEnv: process.env.NODE_ENV,
}) {
  const normalizedOverride = publicOverride?.trim().toLowerCase();

  if (normalizedOverride === "true") {
    return true;
  }

  if (normalizedOverride === "false") {
    return false;
  }

  if (normalizedOverride) {
    return false;
  }

  return nodeEnv === "development";
}

export function isSpecialistBookingFrontendPreviewEnabled() {
  return resolveSpecialistBookingFrontendPreviewEnabled();
}

export function canActivateSpecialistBookingFrontendPreviewForSource({
  sourceLocationId,
  sourceUserLocationId,
}: SpecialistBookingFrontendPreviewSource) {
  return (
    isSpecialistBookingFrontendPreviewEnabled() &&
    FRONTEND_PREVIEW_SOURCE_ASSIGNMENTS.has(
      `${sourceLocationId}\u0000${sourceUserLocationId}`,
    )
  );
}

export function canActivateResultsBookingFrontendPreviewForSource({
  sourceLocationId,
  sourceUserLocationId,
}: SpecialistBookingFrontendPreviewSource) {
  return (
    isSpecialistBookingFrontendPreviewEnabled() &&
    RESULTS_FRONTEND_PREVIEW_SOURCE_ASSIGNMENTS.has(
      `${sourceLocationId}\u0000${sourceUserLocationId}`,
    )
  );
}
